import { Request as IttyRequest } from 'itty-router'
import {
    OwnedNftsResponse,
    NftContract,
    OwnedNft,
    OpenSeaCollectionMetadata,
    GetContractsForOwnerResponse,
    NftContractForOwner,
} from 'alchemy-sdk'
import { AuthEnv, Environment } from 'worker-common'
import { z } from 'zod'

export interface Env extends AuthEnv {
    ENVIRONMENT: Environment
    ALCHEMY_API_KEY: string
    CODEX_API_KEY: string
}

export interface TokenProviderRequest extends Request {
    rpcUrl?: string
    authHeader?: string
    params: IttyRequest['params']
    query: IttyRequest['query']
}

export interface IError extends Error {
    status?: number
}

interface AccurateOwnedNft extends OwnedNft {
    contractMetadata: NftContract
}

export interface GetCollectionMetadataInfuraResponse {
    contract: string
    name: string
    symbol: string
    tokenType: TokenType
}

export interface GetCollectionsForOwnerInfuraResponse {
    total: number
    pageNumber: number
    pageSize: number
    network: string
    cursor: null | string
    account: string
    collections: {
        contract: string
        name: string
        symbol: string
        tokenType: TokenType
    }[]
}

export interface GetNftsAlchemyResponse extends OwnedNftsResponse {
    blockHash: string
    ownedNfts: AccurateOwnedNft[]
    pageKey?: string // override readonly
}

export type GetContractsForOwnerAlchemyResponse = Omit<
    GetContractsForOwnerResponse,
    'contracts'
> & {
    contracts: (Omit<NftContractForOwner, 'tokenType'> & { tokenType: TokenType })[]
}

export interface GetContractMetadataAlchemyResponse extends ContractMetadata {
    address: string
    name: string
    symbol: string
    totalSupply?: string
    contractDeployer: string
    deployedBlockNumber: number
    tokenType: TokenType
    openSeaMetadata?: OpenSeaCollectionMetadata & { collectionSlug?: string | null }
}

export interface GetTokenMetadataAlchemyResponse {
    decimals: number
    logo: string
    name: string
    symbol: string
}

// worker response for /getCollectionsForOwner
export type GetCollectionsForOwnerResponse = {
    totalCount: number
    pageKey?: string
    collections: ContractMetadata[]
}

export type GetCollectionsForOwnerAcrossNetworksResponse = {
    chainId: number
    status: 'success' | 'error'
    data?: GetCollectionsForOwnerResponse
    error?: unknown
}

export const GetNftOwnersResponseSchema = z.object({
    owners: z.array(z.string()).optional().nullable(),
})
export type GetNftOwnersResponse = z.infer<typeof GetNftOwnersResponseSchema>

// 7.2.2026 - the alchemy returns a bunch of data
// i made everything optional and nullable from the sample response
// and only included data we actually care about in the client
// sample response https://sandbox.alchemy.com/?network=ETH_MAINNET&method=getNFTMetadata-v3&query.contractAddress=0xe785E82358879F061BC3dcAC6f0444462D4b5330&query.tokenId=44&query.tokenType=&query.tokenUriTimeoutInMs&query.refreshCache=false
export const GetNftMetadataResponseSchema = z.object({
    contract: z.object({
        address: z.string().optional().nullable(),
        // name: z.string(),
        // symbol: z.string(),
        // totalSupply: z.string(),
        // tokenType: z.string(),
        // contractDeployer: z.string(),
        // deployedBlockNumber: z.number(),
        openSeaMetadata: z
            .object({
                // floorPrice: z.number(),
                // collectionName: z.string(),
                // collectionSlug: z.string(),
                // safelistRequestStatus: z.string(),
                imageUrl: z.string().optional().nullable(),
                // description: z.string(),
                // externalUrl: z.string().nullable(),
                // twitterUsername: z.string(),
                // discordUrl: z.string(),
                // bannerImageUrl: z.string(),
                // lastIngestedAt: z.string(),
            })
            .optional()
            .nullable(),
        // isSpam: z.boolean(),
        // spamClassifications: z.array(z.string()),
    }),
    tokenId: z.string().optional().nullable(),
    tokenType: z.string().optional().nullable(),
    name: z.string().optional().nullable(),
    description: z.string().optional().nullable(),
    tokenUri: z.string().optional().nullable(),
    image: z
        .object({
            cachedUrl: z.string().optional().nullable(),
            thumbnailUrl: z.string().optional().nullable(),
            pngUrl: z.string().optional().nullable(),
            contentType: z.string().optional().nullable(),
            size: z.number().optional().nullable(),
            originalUrl: z.string().optional().nullable(),
        })
        .optional()
        .nullable(),
    // animation: z.object({
    //     cachedUrl: z.string().nullable(),
    //     contentType: z.string().nullable(),
    //     size: z.number().nullable(),
    //     originalUrl: z.string().nullable(),
    // }),
    // raw: z
    //     .object({
    //         tokenUri: z.string().optional().nullable(),
    //         metadata: z
    //             .object({
    //                 name: z.string().optional().nullable(),
    //                 image: z.string().optional().nullable(),
    //                 attributes: z
    //                     .array(
    //                         z
    //                             .object({
    //                                 trait_type: z.string().optional().nullable(),
    //                                 value: z.string().optional().nullable(),
    //                             })
    //                             .optional()
    //                             .nullable(),
    //                     )
    //                     .optional()
    //                     .nullable(),
    //             })
    //             .optional()
    //             .nullable(),
    //         error: z.string().optional().nullable(),
    //     })
    //     .optional()
    //     .nullable(),
    // collection: z.object({
    //     name: z.string(),
    //     slug: z.string(),
    //     externalUrl: z.string().nullable(),
    //     bannerImageUrl: z.string(),
    // }),
    // mint: z.object({
    //     mintAddress: z.string().nullable(),
    //     blockNumber: z.number().nullable(),
    //     timestamp: z.string().nullable(),
    //     transactionHash: z.string().nullable(),
    // }),
    owners: z.array(z.string()).optional().nullable(),
    // timeLastUpdated: z.string().optional().nullable(),
})
export type GetNftMetadataResponse = z.infer<typeof GetNftMetadataResponseSchema>

// worker reponse for /getNftsForOwner
export interface GetNftsResponse extends Omit<GetNftsAlchemyResponse, 'ownedNfts'> {
    ownedNftsContract: ContractMetadata[]
}

// worker response for /getContractMetadata
export type GetContractMetadataResponse = ContractMetadata

// This is a type for the client to work with. Reponses to the client should include this type, whether the payload is from Alchemy or Infura
export type ContractMetadata = {
    address?: string | null
    name?: string | null
    symbol?: string | null
    tokenType?: TokenType
    imageUrl?: string | null // from OpenSea data if available
    openSeaCollectionUrl?: string | null
    image?: NftImageMetadata | null
    displayNft?: NftDisplayNft | null
    decimals?: number | null
    tokenId?: string | null
    quantity?: bigint | null
}

export type NftImageMetadata = {
    cachedUrl?: string | null
    thumbnailUrl?: string | null
}

export type NftDisplayNft = {
    tokenId?: string | null
    name?: string | null
}

export type GetCollectionMetadataAcrossNetworksResponse = {
    chainId: number
    data: ContractMetadata
}

// TODO: remove? we probably won't use this endpoint
export type IsHolderOfCollectionAlchemyResponse = { isHolderOfCollection: boolean }
export type IsHolderOfCollectionResponse = IsHolderOfCollectionAlchemyResponse

export enum TokenType {
    ERC1155 = 'ERC1155',
    ERC721 = 'ERC721',
    ERC20 = 'ERC20',
    NOT_A_CONTRACT = 'NOT_A_CONTRACT',
    NO_SUPPORTED_NFT_STANDARD = 'NO_SUPPORTED_NFT_STANDARD',
    UNKNOWN = 'UNKNOWN',
}
